import type { HttpErrorResponse } from '@angular/common/http';
import type { Bucket, Transaction } from '../types';
import type {
  CategorizationRule,
  CategorizationRuleSuggestion,
  CategorizationRuleTransactionType,
} from '../services/categorization-rule.service';

export type RuleCriteria = Pick<CategorizationRule, 'merchant' | 'transactionType' | 'amountMin' | 'amountMax'>;

export interface RuleConflictDetails {
  conflictingRuleId: string;
  conflictingRule?: RuleCriteria & { id: string; bucketId: string };
}

export type RuleEffectiveState = 'active' | 'paused' | 'bucket-archived' | 'bucket-missing';

const wholeCurrency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const centsCurrency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

export function formatRuleAmount(amount: number): string {
  return Number.isInteger(amount) ? wholeCurrency.format(amount) : centsCurrency.format(amount);
}

export function amountRangeLabel(criteria: Pick<RuleCriteria, 'amountMin' | 'amountMax'>): string {
  if (criteria.amountMin == null || criteria.amountMax == null) return 'Any amount';
  if (criteria.amountMin === criteria.amountMax) return `Exactly ${formatRuleAmount(criteria.amountMin)}`;
  if (criteria.amountMin === 0) return `Up to ${formatRuleAmount(criteria.amountMax)}`;
  return `${formatRuleAmount(criteria.amountMin)} – ${formatRuleAmount(criteria.amountMax)}`;
}

export function merchantInitial(merchant: string): string {
  return merchant.trim().charAt(0).toUpperCase() || '?';
}

export function merchantKey(merchant: string): string {
  return merchant.trim().toLocaleLowerCase('en-US');
}

export function ruleTypeLabel(type: CategorizationRuleTransactionType): string {
  return type === 'INCOME' ? 'Income' : 'Expense';
}

export function transactionRuleType(transaction: Pick<Transaction, 'netValue'>): CategorizationRuleTransactionType {
  return transaction.netValue > 0 ? 'INCOME' : 'EXPENSE';
}

export function criteriaMatchTransaction(
  criteria: RuleCriteria,
  transaction: Pick<Transaction, 'merchant' | 'amount' | 'netValue'>,
): boolean {
  if (merchantKey(criteria.merchant) !== merchantKey(transaction.merchant)) return false;
  if (criteria.transactionType !== transactionRuleType(transaction)) return false;
  if (criteria.amountMin == null || criteria.amountMax == null) return true;
  const amount = Math.abs(transaction.amount);
  return amount >= criteria.amountMin && amount <= criteria.amountMax;
}

export type RuleBackfillCandidate = Pick<Transaction, 'merchant' | 'amount' | 'netValue' | 'bucketId' | 'deleted'> &
  Partial<Pick<Transaction, 'bankMeta' | 'parent' | 'children'>>;

/**
 * Mirrors the server's backfill filter: only unsorted, visible, Plaid-imported,
 * non-split transactions can be sorted by a rule. `bankMeta` is present exactly
 * when the server holds a Plaid transaction id for the row itself.
 */
export function isRuleBackfillEligible(transaction: RuleBackfillCandidate): boolean {
  if (transaction.bucketId || transaction.deleted) return false;
  if (transaction.bankMeta == null) return false;
  if (transaction.parent != null) return false;
  return (transaction.children?.length ?? 0) === 0;
}

export function findSuggestionForTransaction(
  suggestions: readonly CategorizationRuleSuggestion[],
  transaction: RuleBackfillCandidate,
): CategorizationRuleSuggestion | null {
  if (!isRuleBackfillEligible(transaction)) return null;
  return suggestions.find(suggestion => criteriaMatchTransaction(suggestion, transaction)) ?? null;
}

export function bucketTypeForRuleType(type: CategorizationRuleTransactionType): 'BUDGET' | 'INCOME' {
  return type === 'INCOME' ? 'INCOME' : 'BUDGET';
}

export function eligibleBucketsForRuleType(buckets: readonly Bucket[], type: CategorizationRuleTransactionType): Bucket[] {
  const bucketType = bucketTypeForRuleType(type);
  return buckets
    .filter(bucket => bucket.type === bucketType && !bucket.archivedMonth)
    .sort((left, right) => left.name.localeCompare(right.name));
}

export function ruleEffectiveState(rule: Pick<CategorizationRule, 'enabled' | 'bucketId'>, bucket: Bucket | undefined): RuleEffectiveState {
  if (!rule.enabled) return 'paused';
  if (!bucket) return 'bucket-missing';
  if (bucket.archivedMonth) return 'bucket-archived';
  return 'active';
}

export function ruleEffectiveStateLabel(state: RuleEffectiveState): string | null {
  switch (state) {
    case 'paused':
      return 'Paused';
    case 'bucket-archived':
      return 'Budget archived';
    case 'bucket-missing':
      return 'Budget removed';
    default:
      return null;
  }
}

export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

export function ruleConflictDetailsFromError(error: unknown): RuleConflictDetails | null {
  const response = error as Partial<HttpErrorResponse> | null;
  if (!response || response.status !== 409) return null;
  const payload = response.error as { errors?: unknown; details?: unknown } | null | undefined;
  const firstError = Array.isArray(payload?.errors) ? (payload?.errors[0] as { details?: unknown } | undefined) : undefined;
  const details = Array.isArray(firstError?.details)
    ? firstError.details
    : Array.isArray(payload?.details)
      ? payload.details
      : [];
  const conflict = details.find(
    (detail): detail is { conflictingRuleId: string; conflictingRule?: RuleConflictDetails['conflictingRule'] } =>
      typeof detail === 'object' &&
      detail !== null &&
      typeof (detail as { conflictingRuleId?: unknown }).conflictingRuleId === 'string',
  );
  if (!conflict) return { conflictingRuleId: '' };
  return { conflictingRuleId: conflict.conflictingRuleId, conflictingRule: conflict.conflictingRule };
}

export function suggestionMovesBudget(suggestion: CategorizationRuleSuggestion | null): boolean {
  const proposal = suggestion?.proposal;
  return (
    proposal?.action === 'update' &&
    proposal.currentBucketId != null &&
    proposal.currentBucketId !== suggestion?.bucketId
  );
}

export function suggestionChangesAmounts(suggestion: CategorizationRuleSuggestion | null): boolean {
  const proposal = suggestion?.proposal;
  return (
    proposal?.action === 'update' &&
    (proposal.amountMin !== proposal.currentAmountMin || proposal.amountMax !== proposal.currentAmountMax)
  );
}
